import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Loading from '../../components/common/Loading';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import axios from 'axios';

const propTypes = {
  user: PropTypes.object.isRequired
};

function BulkSalesRelease({ user }) {
  const [retailers, setRetailers] = useState([]);
  const [selectedRetailerIds, setSelectedRetailerIds] = useState(new Set());
  const [loadingRetailers, setLoadingRetailers] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [releaseDorisStatus, setReleaseDorisStatus] = useState('idle');
  const [releaseDorisMessage, setReleaseDorisMessage] = useState('');
  const [releaseElasticStatus, setReleaseElasticStatus] = useState('idle');
  const [releaseElasticMessage, setReleaseElasticMessage] = useState('');

  const [dorisResults, setDorisResults] = useState(null);
  const [elasticResults, setElasticResults] = useState(null);

  const buttonClassName = 'reclassify__button sl-delete-button sl-delete-button--sm';

  useEffect(() => {
    const fetchRetailers = async () => {
      try {
        const res = await axios.get('/api/atlas/GetSalesRetailers');
        setRetailers(res.data.retailers || []);
      } catch (err) {
        console.error('Error fetching sales retailers:', err);
        setLoadError('Failed to load retailers. Please refresh the page.');
      } finally {
        setLoadingRetailers(false);
      }
    };
    fetchRetailers();
  }, []);

  const handleToggleRetailer = (retailerId) => {
    setSelectedRetailerIds((prev) => {
      const next = new Set(prev);
      if (next.has(retailerId)) {
        next.delete(retailerId);
      } else {
        next.add(retailerId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedRetailerIds.size === retailers.length) {
      setSelectedRetailerIds(new Set());
    } else {
      setSelectedRetailerIds(new Set(retailers.map((r) => r.retailerId)));
    }
  };

  const selectedIds = Array.from(selectedRetailerIds);

  const handleReleaseDoris = async () => {
    if (selectedIds.length === 0) return;
    setReleaseDorisStatus('processing');
    setReleaseDorisMessage('');
    setDorisResults(null);

    try {
      const response = await axios.post('/api/atlas/ReleaseSalesData', {
        retailerIds: selectedIds
      });

      if (response.data.success === false) {
        setReleaseDorisStatus('error');
        setReleaseDorisMessage(response.data.message || 'Release failed');
      } else {
        setReleaseDorisStatus('done');
        setReleaseDorisMessage(response.data.message || 'Done!');
      }
      setDorisResults(response.data.releasedRetailers || []);
    } catch (error) {
      console.error('Error releasing Doris sales data:', error);
      setReleaseDorisStatus('error');
      setReleaseDorisMessage('Request failed. Check console for details.');
    }
  };

  const handleReleaseElastic = async () => {
    if (selectedIds.length === 0) return;
    setReleaseElasticStatus('processing');
    setReleaseElasticMessage('');
    setElasticResults(null);

    const results = [];
    let hasError = false;

    for (const retailerId of selectedIds) {
      try {
        await axios.post(`/api/admin/ReleaseAtlasSales?retailerId=${retailerId}`);
        results.push({ retailerId, success: true });
      } catch (error) {
        console.error(`Error releasing Elastic sales for retailer ${retailerId}:`, error);
        results.push({ retailerId, success: false, error: error.message });
        hasError = true;
      }
    }

    setElasticResults(results);
    if (hasError) {
      const failedCount = results.filter((r) => !r.success).length;
      setReleaseElasticStatus('error');
      setReleaseElasticMessage(`Completed with ${failedCount} error(s) out of ${selectedIds.length} retailer(s).`);
    } else {
      setReleaseElasticStatus('done');
      setReleaseElasticMessage(`Successfully released Elastic sales for ${selectedIds.length} retailer(s).`);
    }
  };

  const getRetailerName = (retailerId) => {
    const r = retailers.find((ret) => ret.retailerId === retailerId);
    return r ? r.name : `Retailer ${retailerId}`;
  };

  if (!user) {
    return <Loading className="spinner" />;
  }

  return (
    <div className="team">
      <h4 className="sl-header-text">Bulk Sales Release</h4>
      <hr className="sl-divider sl-divider--no-margin-top" />

      {loadingRetailers && <Loading className="spinner" />}
      {loadError && <div style={{ color: 'red', marginBottom: '16px' }}>{loadError}</div>}

      {!loadingRetailers && !loadError && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={retailers.length > 0 && selectedRetailerIds.size === retailers.length}
                  indeterminate={selectedRetailerIds.size > 0 && selectedRetailerIds.size < retailers.length}
                  onChange={handleSelectAll}
                />
              }
              label={<strong>Select All ({retailers.length} retailers)</strong>}
            />
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '4px 16px',
              maxHeight: '360px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '24px'
            }}
          >
            {retailers.map((r) => (
              <FormControlLabel
                key={r.retailerId}
                control={
                  <Checkbox
                    checked={selectedRetailerIds.has(r.retailerId)}
                    onChange={() => handleToggleRetailer(r.retailerId)}
                    size="small"
                  />
                }
                label={`${r.name} (${r.retailerId})`}
                style={{ margin: 0 }}
              />
            ))}
          </div>

          <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
            {selectedRetailerIds.size} retailer(s) selected
          </div>

          <h4 className="sl-header-text" style={{ marginTop: '24px' }}>Release Doris Sales Data</h4>
          <hr className="sl-divider sl-divider--no-margin-top" />
          <Button
            className={buttonClassName}
            disabled={selectedIds.length === 0 || releaseDorisStatus === 'processing'}
            onClick={handleReleaseDoris}
          >
            {releaseDorisStatus === 'processing'
              ? `Releasing Doris for ${selectedIds.length} retailer(s)...`
              : `Release Doris Sales (${selectedIds.length} retailers)`}
          </Button>
          {releaseDorisMessage && (
            <div
              style={{
                marginTop: '8px',
                color: releaseDorisStatus === 'error' ? 'red' : 'green',
                fontWeight: 'bold'
              }}
            >
              {releaseDorisMessage}
            </div>
          )}
          {dorisResults && dorisResults.length > 0 && (
            <div style={{ marginTop: '8px', fontSize: '13px' }}>
              {dorisResults.map((r) => (
                <div key={r.retailerId} style={{ color: r.success ? 'green' : 'red' }}>
                  {getRetailerName(r.retailerId)} ({r.retailerId}): {r.success ? 'Success' : r.errorMessage || 'Failed'}
                </div>
              ))}
            </div>
          )}

          <h4 className="sl-header-text" style={{ marginTop: '24px' }}>Release Elastic Sales Data</h4>
          <hr className="sl-divider sl-divider--no-margin-top" />
          <Button
            className={buttonClassName}
            disabled={selectedIds.length === 0 || releaseElasticStatus === 'processing'}
            onClick={handleReleaseElastic}
          >
            {releaseElasticStatus === 'processing'
              ? `Releasing Elastic for ${selectedIds.length} retailer(s)...`
              : `Release Elastic Sales (${selectedIds.length} retailers)`}
          </Button>
          {releaseElasticMessage && (
            <div
              style={{
                marginTop: '8px',
                color: releaseElasticStatus === 'error' ? 'red' : 'green',
                fontWeight: 'bold'
              }}
            >
              {releaseElasticMessage}
            </div>
          )}
          {elasticResults && elasticResults.length > 0 && (
            <div style={{ marginTop: '8px', fontSize: '13px' }}>
              {elasticResults.map((r) => (
                <div key={r.retailerId} style={{ color: r.success ? 'green' : 'red' }}>
                  {getRetailerName(r.retailerId)} ({r.retailerId}): {r.success ? 'Success' : r.error || 'Failed'}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

BulkSalesRelease.propTypes = propTypes;

export default BulkSalesRelease;
